import { ImageItem, TemplateDto, TextItem, extractPreviewElements } from "@exibra/shared";
import { useMemo, useRef, useState, useEffect, CSSProperties } from "react";
import { Stage, Layer, Text, Image } from "react-konva";

const ASPECT_RATIO = 16 / 9;
const BASE_WIDTH = 1920; // Base width used in the editor

/**
 * Previews a template
 */
export const TemplatePreview = ({
    template,
    style,
    placeholderData,
}: {
    template: TemplateDto;
    style?: CSSProperties;
    placeholderData: Record<string, string>;
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [stageSize, setStageSize] = useState({ width: 0, height: 0 });
    const [images, setImages] = useState<Record<string, HTMLImageElement>>({});

    // Calculate scale factor based on original design size vs current stage size
    const scaleFactor = stageSize.width > 0 ? stageSize.width / BASE_WIDTH : 0;

    const previewElements = useMemo(() => extractPreviewElements(template.scene), [template.scene]);

    // Preload images
    useEffect(() => {
        const imageElements =
            previewElements.filter(
                (element): element is ImageItem => element.type === "image",
            ) || [];

        imageElements.forEach((element) => {
            if (!images[element.id] && element.imageUrl) {
                const img = new window.Image();
                const imageUrl = (element as ImageItem).imageUrl;
                img.src = !imageUrl.startsWith("http")
                    ? `${import.meta.env.VITE_API_URL}${imageUrl}`
                    : imageUrl;
                img.onload = () => {
                    setImages((prev) => ({
                        ...prev,
                        [element.id]: img,
                    }));
                };
            }
        });
    }, [previewElements, images]);

    useEffect(() => {
        const updateDimensions = () => {
            if (!containerRef.current) return;

            const containerWidth = containerRef.current.clientWidth;
            const containerHeight = containerRef.current.clientHeight;

            if (containerWidth <= 0) return;

            // Calculate dimensions that maintain 16:9 aspect ratio while fitting in container
            let width, height;

            // Check which dimension is the limiting factor
            if (containerWidth / containerHeight > ASPECT_RATIO) {
                // Container is wider than needed for aspect ratio, so height is the limiting factor
                height = containerHeight;
                width = height * ASPECT_RATIO;
            } else {
                // Container is taller than needed for aspect ratio, so width is the limiting factor
                width = containerWidth;
                height = width / ASPECT_RATIO;
            }

            setStageSize({ width: containerWidth, height: containerWidth / ASPECT_RATIO });
        };

        // Initial calculation
        updateDimensions();

        // Use ResizeObserver for reliable size detection
        const resizeObserver = new ResizeObserver(updateDimensions);
        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }

        window.addEventListener("resize", updateDimensions);

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener("resize", updateDimensions);
        };
    }, []);

    return (
        <div ref={containerRef} className="flex items-center justify-center" style={style}>
            {stageSize.width > 0 && stageSize.height > 0 && (
                <Stage
                    width={stageSize.width}
                    height={stageSize.height}
                    style={{
                        display: "block",
                        margin: "0 auto",
                    }}
                >
                    <Layer>
                        {[...previewElements].reverse().map((element) => {
                            // Apply scaling to position and dimensions
                            const x = (element.x || 0) * scaleFactor;
                            const y = (element.y || 0) * scaleFactor;

                            if (element.type === "text") {
                                const textElement = element as TextItem;
                                return (
                                    <Text
                                        key={element.id}
                                        text={
                                            textElement.isPlaceholder
                                                ? placeholderData[textElement.id] ||
                                                  `[${textElement.text}]`
                                                : textElement.text
                                        }
                                        x={x}
                                        y={y}
                                        offsetX={0}
                                        offsetY={0}
                                        fill={"#000000"}
                                        width={
                                            textElement.width
                                                ? textElement.width * scaleFactor
                                                : undefined
                                        }
                                        opacity={
                                            textElement.isPlaceholder &&
                                            !placeholderData[textElement.id]
                                                ? 0.5
                                                : 1
                                        }
                                        fontSize={textElement.style.fontSize * scaleFactor}
                                        fontStyle={textElement.style.italic ? "italic" : "normal"}
                                        fontFamily={textElement.style.font || "Arial"}
                                        fontVariant={textElement.style.bold ? "bold" : "normal"}
                                        align={textElement.style.align}
                                        rotation={textElement.rotation || 0}
                                    />
                                );
                            }

                            if (element.type === "image") {
                                const imageElement = element as ImageItem;
                                const image = images[element.id];
                                if (!image) return null;

                                // Handle optional width/height with defaults
                                const width = (imageElement.width || 100) * scaleFactor;
                                const height = (imageElement.height || 100) * scaleFactor;

                                return (
                                    <Image
                                        key={element.id}
                                        image={image}
                                        x={x}
                                        y={y}
                                        width={width}
                                        height={height}
                                        rotation={imageElement.rotation || 0}
                                        scaleX={imageElement.scaleX || 1}
                                        scaleY={imageElement.scaleY || 1}
                                    />
                                );
                            }
                            return null;
                        })}
                    </Layer>
                </Stage>
            )}
        </div>
    );
};
